import { Link } from "wouter";
import { SEOHead, ORGANIZATION_SCHEMA } from "@/components/seo-head";
import { SeoPageHeader } from "@/components/seo-page-header";
import { SeoPageFooter } from "@/components/seo-page-footer";
import { Button } from "@/components/ui/button";
import { ChevronRight, ArrowRight } from "lucide-react";

const PAGE_URL = "https://thepropertool.com/glossary";

interface GlossaryTerm {
  term: string;
  slug: string;
  category: "Estimating & Pricing" | "Construction Types" | "Trades & Labour" | "Legal & Compliance" | "Software & Tech";
  definition: string;
  relatedHref?: string;
  relatedLabel?: string;
}

export const GLOSSARY_TERMS: GlossaryTerm[] = [
  {
    term: "Bill of Quantities (BoQ)",
    slug: "bill-of-quantities",
    category: "Estimating & Pricing",
    definition: "A structured document that lists every item of work, material, and labour required to complete a construction project, with quantities and unit rates. It forms the basis of competitive tendering and is prepared by a quantity surveyor from the architect's drawings and specification. Builders price each line item to produce their total tender sum.",
    relatedHref: "/construction-estimating-software",
    relatedLabel: "Cost estimating software",
  },
  {
    term: "Contingency",
    slug: "contingency",
    category: "Estimating & Pricing",
    definition: "A percentage added to a construction cost estimate to cover unforeseen risks, site conditions, or scope changes. A typical contingency on a residential project is 5–15% of the total build cost, depending on the complexity and the amount of information available at tender stage. Contingency is not profit - it is risk provision.",
    relatedHref: "/blog/how-to-price-building-jobs",
    relatedLabel: "How to price building jobs",
  },
  {
    term: "Dayworks",
    slug: "dayworks",
    category: "Estimating & Pricing",
    definition: "A method of pricing construction work based on actual time and materials used, rather than a fixed price. Dayworks rates (labour, plant, and materials) are agreed in advance and recorded on daywork sheets, which the client or their representative signs off daily. Used for instructed variations where it is impractical to agree a fixed price beforehand.",
  },
  {
    term: "Elemental Cost Plan",
    slug: "elemental-cost-plan",
    category: "Estimating & Pricing",
    definition: "An early-stage budget broken down by building element (substructure, superstructure, finishes, services, external works, etc.) rather than by trade or work package. Used at RIBA stages 2–3 to test design decisions against budget before detailed drawings are produced. Each element is expressed as a cost per m² of gross internal floor area.",
  },
  {
    term: "Gross Internal Floor Area (GIFA)",
    slug: "gross-internal-floor-area",
    category: "Estimating & Pricing",
    definition: "The total floor area of a building measured to the internal faces of the external walls, including all internal walls, columns, and stairwells, but excluding external walls themselves. GIFA is the standard measurement basis for cost benchmarking and elemental cost plans in the UK, as defined by BCIS.",
    relatedHref: "/ai-takeoff-software",
    relatedLabel: "AI takeoff software",
  },
  {
    term: "Labour Rate",
    slug: "labour-rate",
    category: "Estimating & Pricing",
    definition: "The hourly or daily cost of a tradesperson used in a construction estimate. An all-in labour rate includes the base wage, employer's National Insurance contributions, holiday pay, and on-costs such as PPE and training. UK all-in labour rates vary by trade and region - for example, a skilled carpenter might range from £180 to £280 per day depending on location.",
    relatedHref: "/blog/how-to-price-building-jobs",
    relatedLabel: "How to price building jobs",
  },
  {
    term: "Mark-up",
    slug: "mark-up",
    category: "Estimating & Pricing",
    definition: "The percentage added to direct costs (labour, materials, plant, and subcontractors) to recover overhead and generate profit. Mark-up is expressed as a percentage of cost; margin is expressed as a percentage of selling price. A 25% mark-up on £80,000 of costs gives a tender price of £100,000, which represents a 20% gross margin.",
  },
  {
    term: "On-costs",
    slug: "on-costs",
    category: "Estimating & Pricing",
    definition: "Employer costs associated with directly employed labour that are additional to the basic wage. In UK construction, on-costs typically include employer's National Insurance (13.8%), apprenticeship levy (0.5%), holiday pay, sick pay, tool allowances, and any CIJC working rule agreement additions. All-in labour rates incorporate on-costs so estimates reflect the true cost of employment.",
  },
  {
    term: "Preliminaries",
    slug: "preliminaries",
    category: "Estimating & Pricing",
    definition: "Costs that relate to the management and running of a construction project rather than permanent work. Preliminaries include site management, temporary site accommodation, welfare facilities, temporary services (power, water), hoardings, scaffolding (when treated as a project-wide item), insurance, and health and safety provision. They typically represent 10–20% of the contract sum on residential projects.",
    relatedHref: "/construction-estimating-software",
    relatedLabel: "Cost estimating software",
  },
  {
    term: "Prime Cost (PC) Sum",
    slug: "prime-cost-sum",
    category: "Estimating & Pricing",
    definition: "A provisional allowance included in a bill of quantities or contract for specific materials or goods to be selected by the client or architect after the contract is placed. Common examples include sanitaryware, kitchen units, and ironmongery. The contractor prices for fixing and profit on top of the PC sum, so the final cost depends on the client's selection.",
  },
  {
    term: "Provisional Sum",
    slug: "provisional-sum",
    category: "Estimating & Pricing",
    definition: "An allowance included in a contract sum for work whose scope or cost cannot be accurately determined at tender stage. Unlike a prime cost sum, which covers specific goods, a provisional sum covers complete work items - for example, 'archaeological investigation: £5,000 provisional'. The sum is replaced with actual costs once the work is instructed and measured.",
  },
  {
    term: "Rate",
    slug: "rate",
    category: "Estimating & Pricing",
    definition: "The unit price for a specific item of work, expressed per unit of measurement (e.g. £/m², £/m, £/nr). Rates are built up from labour, materials, and plant components, to which overhead and profit are added. A well-maintained rate library allows estimators to price consistently across multiple projects without rebuilding costs from scratch each time.",
    relatedHref: "/estimating-software-for-builders",
    relatedLabel: "Estimating software for builders",
  },
  {
    term: "Retention",
    slug: "retention",
    category: "Estimating & Pricing",
    definition: "A percentage of each interim payment withheld by the client as security against defects or incomplete work. A typical retention rate is 5%, half of which is released on practical completion and the remainder released at the end of the defects liability period (usually 12 months). Retention is standard in JCT and NEC contracts on larger projects.",
  },
  {
    term: "Scope of Works",
    slug: "scope-of-works",
    category: "Estimating & Pricing",
    definition: "A written description of exactly what work is included in a contract or quotation. A clear scope of works lists every trade, every element of the work, and any specific materials or standards required. It should also state clearly what is excluded. A well-written scope of works reduces disputes, protects the builder if instructed to do additional work, and helps the client compare quotes on a like-for-like basis.",
    relatedHref: "/blog/extension-quote-template-uk",
    relatedLabel: "Extension quote template",
  },
  {
    term: "Tender",
    slug: "tender",
    category: "Estimating & Pricing",
    definition: "A formal offer to carry out construction work at a stated price, submitted in response to an invitation to tender. On larger projects, multiple contractors submit tenders in a competitive process. The tender documents typically include drawings, specification, and a bill of quantities. Tendering is distinct from quoting - tenders are usually more formal, involve greater detail, and may follow a procurement process with specific rules.",
  },
  {
    term: "Unit Rate",
    slug: "unit-rate",
    category: "Estimating & Pricing",
    definition: "The all-in price per unit of measurement for a specific construction item, inclusive of labour, materials, plant, overhead, and profit. For example, a unit rate for block cavity wall might be expressed as £/m². Unit rates are the building blocks of a bill of quantities and allow estimators to produce a total price by multiplying rates by measured quantities.",
  },
  {
    term: "Variation",
    slug: "variation",
    category: "Estimating & Pricing",
    definition: "An instruction to carry out work that differs from, or adds to, the original contract scope. Variations must be instructed in writing under most standard contracts. The cost of a variation is agreed either by applying bill rates to measured quantities, by dayworks, or by lump-sum agreement. Unmanaged variations are one of the most common causes of final account disputes.",
  },

  {
    term: "Extension",
    slug: "extension",
    category: "Construction Types",
    definition: "An addition built onto an existing dwelling to increase habitable floor space. Extensions in the UK are categorised as single-storey (most common), two-storey, side-return, wraparound, or infill. They typically require building regulations approval and often require planning permission unless they fall within permitted development rights. Build costs vary significantly by size, specification, and location.",
    relatedHref: "/blog/extension-quote-template-uk",
    relatedLabel: "Extension quote template",
  },
  {
    term: "Groundworks",
    slug: "groundworks",
    category: "Construction Types",
    definition: "The package of construction work carried out at or below ground level before the main structure is built. Groundworks include site clearance, excavation, temporary drainage, foundations (strip, pad, or raft), ground beams, below-ground drainage, and concrete slabs. Groundworks are typically the first trade on site and their cost and programme depend heavily on ground conditions.",
    relatedHref: "/auto-quote",
    relatedLabel: "Auto Quote: AI scope generation",
  },
  {
    term: "Loft Conversion",
    slug: "loft-conversion",
    category: "Construction Types",
    definition: "The process of converting an existing roof space into habitable accommodation. Common types include dormer (the most common - extends the existing roof slope), hip-to-gable (restructures the end roof), mansard (restructures most of the roof pitch), and Velux (rooflight only, no structural change). Loft conversions require building regulations approval and structural engineer input for steel beams and floor joists.",
    relatedHref: "/blog/loft-conversion-cost-estimate-uk",
    relatedLabel: "How to price a loft conversion",
  },
  {
    term: "New Build",
    slug: "new-build",
    category: "Construction Types",
    definition: "A property constructed from scratch on a cleared or greenfield site, as distinct from a conversion or extension. New build residential projects in the UK are subject to full planning permission and building regulations, and typically follow NHBC warranty requirements if the developer registers with the scheme. Costs are quoted per m² of gross internal floor area and vary by house type, specification, and region.",
  },
  {
    term: "Permitted Development (PD)",
    slug: "permitted-development",
    category: "Construction Types",
    definition: "Certain types of building work that can be carried out on a dwelling without the need for a formal planning application, provided they fall within defined limits. Under current PD rights in England, householders can extend a detached house up to 8m to the rear (single-storey) without planning permission, subject to conditions. PD rights do not remove the requirement for building regulations approval.",
  },
  {
    term: "Refurbishment",
    slug: "refurbishment",
    category: "Construction Types",
    definition: "The process of renovating, updating, or restoring an existing building to improve its condition, appearance, or performance. Refurbishment work ranges from light cosmetic works (redecoration, new kitchen) through to full strip-out and rebuild of internal finishes, services, and structure. Pricing refurbishment is typically harder than new build because of unknown existing conditions.",
  },
  {
    term: "Retrofit",
    slug: "retrofit",
    category: "Construction Types",
    definition: "The upgrading of an existing building's thermal performance, airtightness, ventilation, or energy systems to improve energy efficiency and reduce carbon emissions. Common retrofit measures include external or internal wall insulation, loft insulation, underfloor insulation, triple glazing, heat pumps, and mechanical ventilation with heat recovery (MVHR). Retrofit work in the UK may qualify for VAT relief.",
  },
  {
    term: "Shell and Core",
    slug: "shell-and-core",
    category: "Construction Types",
    definition: "A construction specification where the contractor completes the building structure, external envelope, core areas (lifts, stairs, toilets), and base-build services, but leaves the internal fit-out for the tenant or occupier. Common in commercial and mixed-use developments. The tenant then carries out a Category A or Category B fit-out to their own specification.",
  },

  {
    term: "Bricklayer",
    slug: "bricklayer",
    category: "Trades & Labour",
    definition: "A tradesperson who lays bricks, blocks, and stonework to construct walls, chimneys, and other masonry structures. In the UK, bricklayers typically work to output rates of 200–500 bricks laid per day depending on complexity, bond type, and joint finish. On residential extensions, bricklaying often includes cavity wall construction, lintels, and insulation installation.",
  },
  {
    term: "Carpenter / Joiner",
    slug: "carpenter-joiner",
    category: "Trades & Labour",
    definition: "Carpenters work on site fixing structural timber (first fix), including roof timbers, floor joists, studwork partitions, and window/door frames. Joiners traditionally work in a workshop producing components such as staircases, door frames, and fitted furniture. In practice, many UK tradespeople do both. Second-fix carpentry covers skirting, architrave, door hanging, and fitted joinery.",
  },
  {
    term: "Groundworker",
    slug: "groundworker",
    category: "Trades & Labour",
    definition: "A trade specialising in earthworks, foundations, drainage, and concrete. Groundworkers are typically the first trade on site and the last to leave - they install underground drainage at the start and lay external paving and landscaping at the end. Groundworks pricing is highly sensitive to soil type, groundwater, and access conditions.",
  },
  {
    term: "M&E (Mechanical & Electrical)",
    slug: "mechanical-electrical",
    category: "Trades & Labour",
    definition: "The building services trades covering heating, ventilation, plumbing, gas, and electrical systems. M&E first fix runs cables, pipes, and ductwork before walls are plastered; M&E second fix adds sockets, switches, sanitaryware, radiators, and the boiler after finishes are complete. M&E is typically subcontracted to specialist firms. On a UK residential extension, M&E typically represents 15–25% of the build cost.",
    relatedHref: "/auto-quote",
    relatedLabel: "Auto Quote covers M&E",
  },
  {
    term: "Plasterer",
    slug: "plasterer",
    category: "Trades & Labour",
    definition: "A tradesperson who applies plaster or render to walls and ceilings to produce a smooth finish for decoration. In UK residential construction, two main systems are used: wet plaster (sand-and-cement scratch coat, then finish plaster) and dry lining (plasterboard fixed to studwork or dot-and-dabbed to blockwork, skimmed with finish plaster). Plastering rates are expressed per m² of wall or ceiling area.",
  },
  {
    term: "Plant",
    slug: "plant",
    category: "Trades & Labour",
    definition: "Machinery and equipment used on a construction site, either owned by the contractor or hired in. Common plant items include excavators, dumpers, concrete mixers, generators, compressors, mobile elevated work platforms (MEWPs), tower cranes, and telehandlers. Plant costs are a significant component of groundworks and civil engineering estimates and are usually priced separately from labour and materials.",
  },
  {
    term: "Scaffold",
    slug: "scaffold",
    category: "Trades & Labour",
    definition: "A temporary structure providing a safe working platform for construction, maintenance, and repair work. In UK residential construction, independent tied scaffolding is most common. Scaffold costs include erection, hire (weekly rate), and strike. Hire periods often overrun on residential projects. Scaffold is sometimes included in preliminaries and sometimes in the relevant trade package (e.g. brickwork or roofing).",
  },
  {
    term: "Steelwork / Structural Steel",
    slug: "structural-steel",
    category: "Trades & Labour",
    definition: "Hot-rolled steel sections (typically universal beams, universal columns, and hollow sections) used to support loads in building structures. In UK residential extensions and loft conversions, structural steel is commonly used to support new openings, carry loads from above, and form roof structures. Sizes and specifications are determined by a structural engineer. Steelwork pricing covers supply, delivery, fabrication (if required), and installation.",
    relatedHref: "/blog/how-to-estimate-structural-steel-costs",
    relatedLabel: "How to estimate steelwork costs",
  },
  {
    term: "Subcontractor",
    slug: "subcontractor",
    category: "Trades & Labour",
    definition: "A specialist trade or services firm engaged by the main contractor to carry out a defined package of work. Common residential subcontractors include structural engineers, scaffolders, steel erectors, electricians, plumbers, and decorators. The main contractor manages and is responsible for subcontractors' work. A management margin (typically 10–15%) is added to subcontractor costs to cover coordination and liability.",
    relatedHref: "/blog/how-to-price-building-jobs",
    relatedLabel: "How to price building jobs",
  },

  {
    term: "Building Regulations",
    slug: "building-regulations",
    category: "Legal & Compliance",
    definition: "Statutory minimum standards that all new buildings, extensions, and significant alterations in England and Wales must meet. The regulations cover structure, fire safety, energy efficiency, ventilation, drainage, and accessibility, among other matters. Building regulations approval is separate from planning permission - most extension work requires both. Compliance is inspected by a building control officer or an approved inspector.",
  },
  {
    term: "CDM Regulations",
    slug: "cdm-regulations",
    category: "Legal & Compliance",
    definition: "The Construction (Design and Management) Regulations 2015. They place duties on clients, designers, and contractors to manage health and safety risks throughout a project. For domestic projects with a single contractor, the main contractor typically takes on the principal designer and principal contractor duties. Notifiable projects (lasting more than 30 working days or 500 person-days) must be notified to the Health and Safety Executive (HSE).",
  },
  {
    term: "CIS (Construction Industry Scheme)",
    slug: "construction-industry-scheme",
    category: "Legal & Compliance",
    definition: "A UK HMRC scheme that requires contractors to deduct tax at source from payments to subcontractors who are not employed. Standard CIS deduction is 20% for registered subcontractors; 30% for those not registered. Gross payment status is available to subcontractors who meet turnover and compliance thresholds. CIS applies to most construction work in the UK and carries significant penalties for non-compliance.",
  },
  {
    term: "Defects Liability Period",
    slug: "defects-liability-period",
    category: "Legal & Compliance",
    definition: "The period after practical completion during which the contractor is obliged to return to site to remedy defects at their own cost. The standard period under JCT contracts is 12 months. At the end of the period, a schedule of defects is issued, the contractor makes good, and the second half of the retention is released.",
  },
  {
    term: "JCT Contract",
    slug: "jct-contract",
    category: "Legal & Compliance",
    definition: "A suite of standard form building contracts produced by the Joint Contracts Tribunal. JCT contracts are the most widely used form of contract in UK construction. For residential projects, the JCT Homeowner Contract or the JCT Minor Works Contract are most common. The contract sets out the rights and obligations of the client and contractor, including payment terms, variations, and dispute resolution.",
  },
  {
    term: "Practical Completion",
    slug: "practical-completion",
    category: "Legal & Compliance",
    definition: "The point at which a construction project is complete for all practical purposes - the client can take possession and use the building, even if minor snagging items remain. Practical completion triggers the release of the first half of any retention, the start of the defects liability period, and the transfer of insurance responsibility from contractor to client.",
  },
  {
    term: "VAT on Construction",
    slug: "vat-on-construction",
    category: "Legal & Compliance",
    definition: "Most construction work in the UK is standard-rated at 20% VAT. However, certain works qualify for a reduced 5% rate - including conversions of residential buildings to a different number of dwellings, and installations of energy-saving materials. New build dwellings sold by the developer are zero-rated. Builders must be VAT-registered before charging VAT. Confusion over applicable rates is a common source of disputes and HMRC investigations.",
    relatedHref: "/blog/how-to-price-building-jobs",
    relatedLabel: "How to price building jobs",
  },

  {
    term: "AI Estimating",
    slug: "ai-estimating",
    category: "Software & Tech",
    definition: "The use of artificial intelligence to generate construction cost estimates from drawings, photos, or project descriptions. AI estimating tools can read architectural drawings, extract dimensions and quantities, and apply trade-specific rates to produce a structured bill of quantities in minutes. The AI provides a draft estimate that the builder reviews and adjusts - it does not replace professional judgement but dramatically reduces the time spent on first-pass pricing.",
    relatedHref: "/ai-quoting-software",
    relatedLabel: "AI quoting software for UK builders",
  },
  {
    term: "Cost Library",
    slug: "cost-library",
    category: "Software & Tech",
    definition: "A database of unit rates and work item descriptions used by estimating software to price construction projects consistently. A good cost library organises items by trade category, supports Low/Typical/High rate bands to reflect different specifications and market conditions, and allows the builder to override default rates with their own figures. The Proper Tool's cost library contains over 500 items across 31 UK trade categories.",
    relatedHref: "/estimating-software-for-builders",
    relatedLabel: "Estimating software for builders",
  },
  {
    term: "PDF Takeoff",
    slug: "pdf-takeoff",
    category: "Software & Tech",
    definition: "The process of measuring quantities from drawings saved in PDF format. Traditional takeoff involves printing drawings and using a scale rule or planimeter; digital takeoff uses software to measure on-screen. AI-powered takeoff tools can read PDF drawings automatically and extract dimensions, areas, and counts without manual measurement - reducing the risk of transcription errors and saving significant time.",
    relatedHref: "/ai-takeoff-software",
    relatedLabel: "AI takeoff software",
  },
  {
    term: "Pricing Intelligence",
    slug: "pricing-intelligence",
    category: "Software & Tech",
    definition: "A feature of advanced quoting software that tracks which quotes are won and lost, and uses that data to calibrate future estimates toward the builder's actual accepted rates. Over time, pricing intelligence identifies trades or items where rates are consistently too high or too low, helping the builder win more work at the right margin.",
    relatedHref: "/estimating-software-for-builders",
    relatedLabel: "Estimating software for builders",
  },
  {
    term: "Quantity Surveyor (QS)",
    slug: "quantity-surveyor",
    category: "Software & Tech",
    definition: "A construction professional specialising in the financial management of building projects. A QS prepares cost plans, bills of quantities, tender documents, and valuations, and manages the final account. On large projects a QS is employed throughout the project lifecycle. On smaller residential projects, builders often price without a QS - quoting software helps fill this gap by providing structured cost build-ups that match what a QS would produce.",
  },
  {
    term: "Smart Quoting",
    slug: "smart-quoting",
    category: "Software & Tech",
    definition: "An AI-assisted quoting workflow that generates a structured, itemised scope of works from minimal input - a text description, uploaded drawings, or site photos. The AI reads the inputs, identifies the relevant trades and quantities, and applies UK market rates to produce a draft quote in minutes. The builder reviews and edits before exporting a professional PDF. Smart quoting reduces quoting time from hours to under 10 minutes on a typical residential job.",
    relatedHref: "/auto-quote",
    relatedLabel: "Try Auto Quote",
  },
  {
    term: "Takeoff",
    slug: "takeoff",
    category: "Software & Tech",
    definition: "The process of measuring and listing quantities from construction drawings. Takeoff is the first step in preparing a bill of quantities or estimate - without accurate quantities, rates cannot be applied to produce a reliable total cost. Traditionally carried out manually by a quantity surveyor or estimator, takeoff is now increasingly automated using AI tools that read PDF drawings and extract measurements directly.",
    relatedHref: "/ai-takeoff-software",
    relatedLabel: "AI takeoff software",
  },
];

const CATEGORIES = [
  "Estimating & Pricing",
  "Construction Types",
  "Trades & Labour",
  "Legal & Compliance",
  "Software & Tech",
] as const;

const ALPHABET = "ABCDEFGHIJKLMNOPQRSTUVWXYZ".split("");

const FAQ_SCHEMA = {
  "@context": "https://schema.org",
  "@type": "FAQPage",
  "mainEntity": [
    {
      "@type": "Question",
      "name": "What is a bill of quantities in construction?",
      "acceptedAnswer": {
        "@type": "Answer",
        "text": "A bill of quantities (BoQ) is a structured document listing every item of work, material, and labour required to complete a construction project with quantities and unit rates. It forms the basis of competitive tendering and is prepared by a quantity surveyor from the architect's drawings and specification.",
      },
    },
    {
      "@type": "Question",
      "name": "What does preliminaries mean in construction?",
      "acceptedAnswer": {
        "@type": "Answer",
        "text": "Preliminaries are costs that relate to the management and running of a construction project rather than the permanent work itself. They include site management, temporary accommodation, welfare facilities, hoardings, scaffolding, insurance, and health and safety provision. Preliminaries typically represent 10–20% of the contract sum on residential projects.",
      },
    },
    {
      "@type": "Question",
      "name": "What is a provisional sum in construction?",
      "acceptedAnswer": {
        "@type": "Answer",
        "text": "A provisional sum is an allowance included in a contract sum for work whose scope or cost cannot be accurately determined at tender stage. It is replaced with actual costs once the work is instructed and measured. Unlike a prime cost sum (which covers specific goods), a provisional sum covers complete work items.",
      },
    },
    {
      "@type": "Question",
      "name": "What are groundworks in construction?",
      "acceptedAnswer": {
        "@type": "Answer",
        "text": "Groundworks are the package of construction work carried out at or below ground level before the main structure is built, including site clearance, excavation, foundations, below-ground drainage, and concrete slabs. Groundworks are typically the first trade on site, and their cost depends heavily on ground conditions.",
      },
    },
    {
      "@type": "Question",
      "name": "What is CIS in construction?",
      "acceptedAnswer": {
        "@type": "Answer",
        "text": "CIS stands for Construction Industry Scheme - a UK HMRC scheme requiring contractors to deduct tax at source from payments to unregistered subcontractors. The standard deduction is 20% for registered subcontractors. It applies to most construction work in the UK and carries significant penalties for non-compliance.",
      },
    },
    {
      "@type": "Question",
      "name": "What is practical completion in construction?",
      "acceptedAnswer": {
        "@type": "Answer",
        "text": "Practical completion is the point at which a construction project is complete for all practical purposes and the client can take possession and use the building. It triggers the release of the first half of any retention, the start of the defects liability period, and the transfer of insurance responsibility from contractor to client.",
      },
    },
    {
      "@type": "Question",
      "name": "What is retention in construction?",
      "acceptedAnswer": {
        "@type": "Answer",
        "text": "Retention is a percentage of each interim payment withheld by the client as security against defects or incomplete work. A typical retention rate is 5% - half is released at practical completion and the remainder at the end of the defects liability period (usually 12 months after completion).",
      },
    },
    {
      "@type": "Question",
      "name": "What is a scope of works in construction?",
      "acceptedAnswer": {
        "@type": "Answer",
        "text": "A scope of works is a written description of exactly what work is included in a contract or quotation, listing every trade, every element of the work, and specific materials or standards required. It should also clearly state what is excluded. A well-written scope reduces disputes and protects the builder if instructed to carry out additional work.",
      },
    },
    {
      "@type": "Question",
      "name": "What is AI estimating in construction?",
      "acceptedAnswer": {
        "@type": "Answer",
        "text": "AI estimating uses artificial intelligence to generate construction cost estimates from drawings, photos, or project descriptions. AI estimating tools can read architectural drawings, extract dimensions and quantities, and apply trade-specific rates to produce a structured estimate in minutes. The builder reviews and adjusts the output - AI doesn't replace professional judgement but dramatically reduces first-pass pricing time.",
      },
    },
    {
      "@type": "Question",
      "name": "What is a quantity surveyor?",
      "acceptedAnswer": {
        "@type": "Answer",
        "text": "A quantity surveyor (QS) is a construction professional specialising in the financial management of building projects. A QS prepares cost plans, bills of quantities, tender documents, and valuations, and manages the final account. On smaller residential projects, AI quoting software can fill this gap by producing structured cost build-ups that match what a QS would produce.",
      },
    },
  ],
};

const BREADCRUMB_SCHEMA = {
  "@context": "https://schema.org",
  "@type": "BreadcrumbList",
  "itemListElement": [
    { "@type": "ListItem", "position": 1, "name": "Home", "item": "https://thepropertool.com/" },
    { "@type": "ListItem", "position": 2, "name": "Glossary", "item": PAGE_URL },
  ],
};

const WEBPAGE_SCHEMA = {
  "@context": "https://schema.org",
  "@type": "WebPage",
  "name": "Construction Estimating Glossary: 45 Terms Explained",
  "description": "Plain-English definitions of 45 construction estimating terms - from Bill of Quantities and Preliminaries to AI Estimating and Takeoff.",
  "url": PAGE_URL,
  "breadcrumb": BREADCRUMB_SCHEMA,
  "inLanguage": "en-GB",
};

function getFirstLetter(term: string): string {
  return term.charAt(0).toUpperCase();
}

function getActiveLetters(): Set<string> {
  const letters = new Set<string>();
  GLOSSARY_TERMS.forEach((t) => letters.add(getFirstLetter(t.term)));
  return letters;
}

function buildLetterAnchorSet(): Set<string> {
  const emitted = new Set<string>();
  const firstPerLetter = new Set<string>();
  CATEGORIES.forEach((cat) => {
    const sorted = GLOSSARY_TERMS.filter((t) => t.category === cat).sort((a, b) =>
      a.term.localeCompare(b.term)
    );
    sorted.forEach((term) => {
      const letter = getFirstLetter(term.term);
      if (!emitted.has(letter)) {
        emitted.add(letter);
        firstPerLetter.add(term.slug);
      }
    });
  });
  return firstPerLetter;
}

const LETTER_ANCHOR_SLUGS = buildLetterAnchorSet();

export default function GlossaryPage() {
  const activeLetters = getActiveLetters();

  const termsByCategory = CATEGORIES.map((cat) => ({
    category: cat,
    terms: GLOSSARY_TERMS.filter((t) => t.category === cat).sort((a, b) =>
      a.term.localeCompare(b.term)
    ),
  }));

  return (
    <div className="min-h-screen bg-background text-foreground">
      <SEOHead
        title="Construction Estimating Glossary: 45 Terms Explained"
        description="Plain-English definitions of 45 construction estimating and building terms - Bill of Quantities, Preliminaries, Retention, AI Estimating, and more. Built for UK builders."
        canonical="/glossary"
        schema={[BREADCRUMB_SCHEMA, WEBPAGE_SCHEMA, FAQ_SCHEMA, ORGANIZATION_SCHEMA]}
      />

      <SeoPageHeader />

      <main>
        {/* Hero */}
        <section className="py-16 sm:py-20 bg-gradient-to-b from-primary/5 to-background">
          <div className="max-w-4xl mx-auto px-4 sm:px-6">
            <div className="flex items-center gap-2 text-sm text-muted-foreground mb-6">
              <Link href="/" className="hover:text-foreground transition-colors">Home</Link>
              <ChevronRight className="h-4 w-4" />
              <span>Glossary</span>
            </div>
            <h1 className="text-4xl sm:text-5xl font-bold leading-tight tracking-tight mb-4">
              Construction estimating glossary
            </h1>
            <p className="text-lg sm:text-xl text-muted-foreground leading-relaxed max-w-3xl">
              Plain-English definitions of {GLOSSARY_TERMS.length} construction estimating and building terms - from Bill of Quantities to AI Takeoff. Jump to a letter or browse by category.
            </p>
          </div>
        </section>

        {/* A-Z navigation */}
        <section className="sticky top-16 z-40 border-t border-b border-border/40 bg-background/95 backdrop-blur-sm py-3">
          <div className="max-w-4xl mx-auto px-4 sm:px-6">
            <div className="flex flex-wrap gap-1.5">
              {ALPHABET.map((letter) => {
                const isActive = activeLetters.has(letter);
                return isActive ? (
                  <a
                    key={letter}
                    href={`#letter-${letter}`}
                    className="w-7 h-7 flex items-center justify-center rounded text-xs font-semibold bg-primary/10 text-primary hover:bg-primary hover:text-primary-foreground transition-colors"
                  >
                    {letter}
                  </a>
                ) : (
                  <span
                    key={letter}
                    className="w-7 h-7 flex items-center justify-center rounded text-xs text-muted-foreground/40 cursor-default"
                  >
                    {letter}
                  </span>
                );
              })}
            </div>
          </div>
        </section>

        {/* Category sections */}
        <div className="max-w-4xl mx-auto px-4 sm:px-6 py-12 sm:py-16">
          {termsByCategory.map(({ category, terms }) => (
            <section key={category} className="mb-14">
              <h2 className="text-2xl sm:text-3xl font-bold mb-8 pb-3 border-b border-border">
                {category}
              </h2>
              <div className="space-y-10">
                {terms.map((term) => {
                  const letter = getFirstLetter(term.term);
                  const isLetterAnchor = LETTER_ANCHOR_SLUGS.has(term.slug);
                  return (
                    <div
                      key={term.slug}
                      id={term.slug}
                      className="scroll-mt-32"
                    >
                      {isLetterAnchor && (
                        <span id={`letter-${letter}`} className="sr-only" aria-hidden="true" />
                      )}
                      <div className="flex items-start gap-3">
                        <span className="inline-flex items-center justify-center w-8 h-8 rounded bg-primary/10 text-primary text-sm font-bold flex-shrink-0 mt-0.5">
                          {letter}
                        </span>
                        <div className="flex-1 min-w-0">
                          <h3 className="text-lg font-semibold mb-2">{term.term}</h3>
                          <p className="text-muted-foreground leading-relaxed text-sm sm:text-base">
                            {term.definition}
                          </p>
                          {term.relatedHref && term.relatedLabel && (
                            <Link
                              href={term.relatedHref}
                              className="inline-flex items-center gap-1 text-sm text-primary font-medium mt-2 hover:underline"
                            >
                              <ChevronRight className="h-3.5 w-3.5" />
                              {term.relatedLabel}
                            </Link>
                          )}
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </section>
          ))}
        </div>

        {/* Related pages */}
        <section className="py-12 border-t border-border/40 bg-muted/20">
          <div className="max-w-4xl mx-auto px-4 sm:px-6">
            <h2 className="text-xl font-semibold mb-6">Related pages</h2>
            <div className="grid sm:grid-cols-3 gap-4">
              {[
                { href: "/estimating-software-for-builders", label: "Estimating Software for Builders" },
                { href: "/construction-estimating-software", label: "Construction Cost Estimating" },
                { href: "/ai-quoting-software", label: "AI Quoting Software for UK Builders" },
                { href: "/ai-takeoff-software", label: "AI Takeoff Software" },
                { href: "/auto-quote", label: "Auto Quote: under 10 minutes" },
                { href: "/blog/how-to-price-building-jobs", label: "How to Price Building Jobs" },
              ].map((link) => (
                <Link
                  key={link.href}
                  href={link.href}
                  className="flex items-start gap-2 text-sm text-primary hover:underline font-medium"
                >
                  <ChevronRight className="h-4 w-4 flex-shrink-0 mt-0.5" />
                  {link.label}
                </Link>
              ))}
            </div>
          </div>
        </section>

        {/* CTA */}
        <section className="py-16 sm:py-20 border-t border-border/40">
          <div className="max-w-3xl mx-auto px-4 sm:px-6 text-center">
            <h2 className="text-3xl sm:text-4xl font-bold mb-4">Ready to quote faster?</h2>
            <p className="text-lg text-muted-foreground mb-8">
              Stop building estimates from scratch. The Proper Tool generates a fully itemised quote from your drawings or description in under 10 minutes.
            </p>
            <Button size="lg" asChild>
              <Link href="/login">
                Start your free 14-day trial
                <ArrowRight className="ml-2 h-4 w-4" />
              </Link>
            </Button>
            <p className="text-sm text-muted-foreground mt-4">No credit card required · Cancel any time</p>
          </div>
        </section>
      </main>

      <SeoPageFooter />
    </div>
  );
}
